// Comprehensive logging system for the bulk generation system

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4
}

export interface LogEntry {
  timestamp: string
  level: LogLevel
  category: string
  message: string
  data?: any
  userId?: string
  sessionId?: string
  component?: string
  action?: string
  duration?: number
  error?: Error
}

export interface LoggerConfig {
  level: LogLevel
  enableConsole: boolean
  enableStorage: boolean
  enableRemote: boolean
  maxStorageEntries: number
  remoteEndpoint?: string
  categories: string[]
}

class Logger {
  private config: LoggerConfig
  private logs: LogEntry[] = []
  private sessionId: string
  private userId?: string

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO,
      enableConsole: true,
      enableStorage: process.env.NODE_ENV === 'development',
      enableRemote: process.env.NODE_ENV === 'production',
      maxStorageEntries: 1000,
      categories: ['bulk-generation', 'job-management', 'ui', 'api', 'performance'],
      ...config
    }
    
    this.sessionId = this.generateSessionId()
    this.setupErrorHandling()
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private setupErrorHandling(): void {
    // Global error handler
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        this.error('global-error', 'Uncaught error', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error
        })
      })

      window.addEventListener('unhandledrejection', (event) => {
        this.error('global-error', 'Unhandled promise rejection', {
          reason: event.reason
        })
      })
    }
  }

  setUserId(userId: string): void {
    this.userId = userId
  }

  private shouldLog(level: LogLevel, category: string): boolean {
    return level >= this.config.level && this.config.categories.includes(category)
  }

  private createLogEntry(
    level: LogLevel,
    category: string,
    message: string,
    data?: any,
    component?: string,
    action?: string,
    duration?: number,
    error?: Error
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      userId: this.userId,
      sessionId: this.sessionId,
      component,
      action,
      duration,
      error
    }
  }

  private log(entry: LogEntry): void {
    if (!this.shouldLog(entry.level, entry.category)) return

    // Console logging
    if (this.config.enableConsole) {
      this.logToConsole(entry)
    }

    // Storage logging
    if (this.config.enableStorage) {
      this.logToStorage(entry)
    }

    // Remote logging
    if (this.config.enableRemote) {
      this.logToRemote(entry)
    }
  }

  private logToConsole(entry: LogEntry): void {
    const prefix = `[${entry.timestamp}] [${entry.category}] [${LogLevel[entry.level]}]`
    const message = `${prefix} ${entry.message}`
    
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(message, entry.data)
        break
      case LogLevel.INFO:
        console.info(message, entry.data)
        break
      case LogLevel.WARN:
        console.warn(message, entry.data)
        break
      case LogLevel.ERROR:
      case LogLevel.CRITICAL:
        console.error(message, entry.data, entry.error)
        break
    }
  }

  private logToStorage(entry: LogEntry): void {
    this.logs.push(entry)
    
    // Trim logs if exceeding max entries
    if (this.logs.length > this.config.maxStorageEntries) {
      this.logs = this.logs.slice(-this.config.maxStorageEntries)
    }

    // Store in localStorage for persistence
    try {
      const recentLogs = this.logs.slice(-100) // Keep only recent 100 logs in localStorage
      localStorage.setItem('bulk-generation-logs', JSON.stringify(recentLogs))
    } catch (error) {
      console.warn('Failed to store logs in localStorage:', error)
    }
  }

  private async logToRemote(entry: LogEntry): Promise<void> {
    if (!this.config.remoteEndpoint) return

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(entry)
      })
    } catch (error) {
      console.warn('Failed to send log to remote endpoint:', error)
    }
  }

  // Public logging methods
  debug(category: string, message: string, data?: any, component?: string): void {
    const entry = this.createLogEntry(LogLevel.DEBUG, category, message, data, component)
    this.log(entry)
  }

  info(category: string, message: string, data?: any, component?: string): void {
    const entry = this.createLogEntry(LogLevel.INFO, category, message, data, component)
    this.log(entry)
  }

  warn(category: string, message: string, data?: any, component?: string): void {
    const entry = this.createLogEntry(LogLevel.WARN, category, message, data, component)
    this.log(entry)
  }

  error(category: string, message: string, data?: any, component?: string, error?: Error): void {
    const entry = this.createLogEntry(LogLevel.ERROR, category, message, data, component, undefined, undefined, error)
    this.log(entry)
  }

  critical(category: string, message: string, data?: any, component?: string, error?: Error): void {
    const entry = this.createLogEntry(LogLevel.CRITICAL, category, message, data, component, undefined, undefined, error)
    this.log(entry)
  }

  // Performance logging
  startTimer(category: string, action: string, component?: string): () => void {
    const startTime = performance.now()
    
    return () => {
      const duration = performance.now() - startTime
      const entry = this.createLogEntry(
        LogLevel.INFO,
        category,
        `Performance: ${action}`,
        { duration: `${duration.toFixed(2)}ms` },
        component,
        action,
        duration
      )
      this.log(entry)
    }
  }

  // Job-specific logging methods
  jobCreated(jobId: string, parameters: any): void {
    this.info('bulk-generation', 'Job created', {
      jobId,
      parameters
    }, 'BulkGenerationJobManager')
  }

  jobStarted(jobId: string): void {
    this.info('bulk-generation', 'Job started processing', {
      jobId
    }, 'EdgeFunction')
  }

  jobProgress(jobId: string, progress: number, generated: number, total: number): void {
    this.debug('bulk-generation', 'Job progress update', {
      jobId,
      progress,
      generated,
      total
    }, 'EdgeFunction')
  }

  jobCompleted(jobId: string, stats: any): void {
    this.info('bulk-generation', 'Job completed', {
      jobId,
      stats
    }, 'EdgeFunction')
  }

  jobFailed(jobId: string, error: Error, context?: any): void {
    this.error('bulk-generation', 'Job failed', {
      jobId,
      context
    }, 'EdgeFunction', error)
  }

  jobCancelled(jobId: string, reason?: string): void {
    this.info('bulk-generation', 'Job cancelled', {
      jobId,
      reason
    }, 'BulkGenerationJobManager')
  }

  // API logging methods
  apiRequest(method: string, url: string, data?: any): void {
    this.debug('api', `${method} ${url}`, data)
  }

  apiResponse(method: string, url: string, status: number, duration: number): void {
    this.debug('api', `${method} ${url} - ${status}`, {
      status,
      duration: `${duration}ms`
    })
  }

  apiError(method: string, url: string, error: Error): void {
    this.error('api', `${method} ${url} failed`, {
      error: error.message
    }, undefined, error)
  }

  // UI logging methods
  userAction(action: string, component: string, data?: any): void {
    this.debug('ui', `User action: ${action}`, data, component)
  }

  componentMount(component: string): void {
    this.debug('ui', `Component mounted: ${component}`, undefined, component)
  }

  componentUnmount(component: string): void {
    this.debug('ui', `Component unmounted: ${component}`, undefined, component)
  }

  // Utility methods
  getLogs(category?: string, level?: LogLevel): LogEntry[] {
    let filteredLogs = this.logs

    if (category) {
      filteredLogs = filteredLogs.filter(log => log.category === category)
    }

    if (level !== undefined) {
      filteredLogs = filteredLogs.filter(log => log.level >= level)
    }

    return filteredLogs
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2)
  }

  clearLogs(): void {
    this.logs = []
    localStorage.removeItem('bulk-generation-logs')
  }

  getStats(): {
    totalLogs: number
    logsByLevel: Record<string, number>
    logsByCategory: Record<string, number>
    sessionId: string
    userId?: string
  } {
    const logsByLevel: Record<string, number> = {}
    const logsByCategory: Record<string, number> = {}

    this.logs.forEach(log => {
      const levelName = LogLevel[log.level]
      logsByLevel[levelName] = (logsByLevel[levelName] || 0) + 1
      logsByCategory[log.category] = (logsByCategory[log.category] || 0) + 1
    })

    return {
      totalLogs: this.logs.length,
      logsByLevel,
      logsByCategory,
      sessionId: this.sessionId,
      userId: this.userId
    }
  }
}

// Create singleton logger instance
export const logger = new Logger()

// Performance monitoring utilities
export const performanceMonitor = {
  measureRender: (componentName: string) => {
    return logger.startTimer('performance', `render-${componentName}`, componentName)
  },

  measureAsync: async <T>(
    category: string,
    action: string,
    asyncFn: () => Promise<T>,
    component?: string
  ): Promise<T> => {
    const stopTimer = logger.startTimer(category, action, component)
    try {
      const result = await asyncFn()
      stopTimer()
      return result
    } catch (error) {
      stopTimer()
      logger.error(category, `${action} failed`, undefined, component, error as Error)
      throw error
    }
  },

  measureSync: <T>(
    category: string,
    action: string,
    syncFn: () => T,
    component?: string
  ): T => {
    const stopTimer = logger.startTimer(category, action, component)
    try {
      const result = syncFn()
      stopTimer()
      return result
    } catch (error) {
      stopTimer()
      logger.error(category, `${action} failed`, undefined, component, error as Error)
      throw error
    }
  }
}

// Export logger for use in components
export default logger
